import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import AddTimeRowForm from './AddTimeRowForm';
import TimeRecordGrid from './TimeRecordGrid';
import { formatTotal, unitLabel } from './timeUnits';
import TimeWeekPicker from './TimeWeekPicker';
import TimeWeekStatusPill from './TimeWeekStatusPill';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import CopyIcon from 'Assets/icons/copy.svg';

const EMPTY_DAYS = [];
const EMPTY_ROWS = [];
const HIGHLIGHT_MS = 1500;

/**
 * The first validation message the server sent back, or a generic fallback. Every
 * refusal this page can hit — an unknown project, a locked day, a rate card gap,
 * an unresolvable role — arrives as a 422 carrying a sentence written for the
 * user, so throwing that away for "Something went wrong" would hide the only
 * thing that explains the failure.
 */
function refusalMessage(error) {
    const errors = error?.response?.data?.errors;

    if (errors) {
        const first = Object.values(errors)[0];

        if (Array.isArray(first) && first.length > 0) {
            return first[0];
        }
    }

    return error?.response?.data?.message || $t('Something went wrong');
}

function rowKeyFor(eventId, roleName, category) {
    return `${eventId}|${roleName}|${category}`;
}

export default function MyTimeRecordIndex(props) {
    // Seeded from a ?week= link, e.g. the time summary's View/Edit.
    const [requestedWeek, setRequestedWeek] = useState(props.initialWeek || null);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Writes still waiting on a response. A count rather than a flag: with two
    // cells saving at once, the first to settle must not clear the second, or
    // Submit could go out ahead of a write the week is still waiting on.
    const [pendingWrites, setPendingWrites] = useState(0);
    const busy = pendingWrites > 0;
    // Rows the user has added that hold nothing yet. They exist only here until
    // a cell is filled — an empty row is not a record, so nothing is written for
    // one and nothing is left behind if the week is navigated away from.
    const [pendingRows, setPendingRows] = useState([]);
    // The grid's scroll container, so a duplicate pick can jump to the row that
    // already holds that project and role.
    const gridRef = useRef(null);
    // Only the newest write's response may be applied whole. Each response replaces
    // row scalars and week totals, so an earlier request landing last would put
    // back figures that predate the write after it.
    const writeSeq = useRef(0);
    // The newest write per row key, then per day. A superseded cell write is still the
    // last word on its own day unless that day was written again, so its cell
    // is folded in even when its totals are not.
    const cellSeq = useRef({});
    const [highlightedKey, setHighlightedKey] = useState(null);
    const highlightTimer = useRef(null);

    const toast = useToast();

    useEffect(() => () => clearTimeout(highlightTimer.current), []);

    const confirmRemove = useConfirmationModal({
        message: $t('Are you sure you want to remove this row?'),
        warning: $t('The time recorded against it this week is cleared. Days on or before the project lock date are left as they are.'),
    });

    const fetchWeek = useCallback(
        (week) => {
            setLoading(true);

            setError(null);

            return axios
                .get(props.dataUrl, { params: week ? { week } : {} })
                .then(({ data }) => {
                    // A new week shares row keys with the old one, so a write
                    // still in flight from before must not fold into it.
                    cellSeq.current = {};
                    setPayload(data.data);
                    setPendingRows([]);
                    setError(null);
                })
                .catch(() => {
                    setError($t('Something went wrong'));
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [props.dataUrl],
    );

    useEffect(() => {
        fetchWeek(requestedWeek);
    }, [fetchWeek, requestedWeek]);

    const days = payload?.days ?? EMPTY_DAYS;
    const options = payload?.options;
    const serverRows = payload?.rows ?? EMPTY_ROWS;

    const rows = useMemo(() => {
        const existing = new Set(serverRows.map((row) => row.key));

        return [...serverRows, ...pendingRows.filter((row) => !existing.has(row.key))];
    }, [serverRows, pendingRows]);

    const weekIsClosed = days.length > 0 && days.every((day) => day.locked);
    const weekSubmitted = payload?.week?.status === 'submitted';
    // Empty and draft follow the rows on screen rather than the status the week
    // loaded with, so filling a first cell makes the week submittable without a
    // reload. The server re-checks on submit either way.
    const weekStatus = ['empty', 'draft'].includes(payload?.week?.status) ? (serverRows.length > 0 ? 'draft' : 'empty') : payload?.week?.status;
    const canSubmit = weekStatus === 'draft';
    const noCoverage = days.length > 0 && days.every((day) => !day.covered);

    /**
     * Folds one write's response back in: the affected row replaces its old
     * self, or disappears when the write emptied it, and the totals always come
     * from the server rather than being adjusted locally.
     */
    const applyWrite = useCallback((key, data, onlyDate = null) => {
        setPendingRows((previous) => previous.filter((row) => row.key !== key));

        setPayload((previous) => {
            if (!previous) {
                return previous;
            }

            const existing = previous.rows.find((row) => row.key === key);
            const withoutRow = previous.rows.filter((row) => row.key !== key);

            // A cell write merges just the day it wrote. Replacing the whole
            // row would let a response that crossed with a later write put back
            // that later cell's previous value.
            const merged = data.row && onlyDate && existing ? { ...data.row, cells: { ...existing.cells, [onlyDate]: data.row.cells[onlyDate] } } : data.row;

            return {
                ...previous,
                rows: merged
                    ? [...withoutRow, merged].sort((a, b) => a.event.name.localeCompare(b.event.name) || a.role_name.localeCompare(b.role_name))
                    : withoutRow,
                daily_totals: data.daily_totals ?? previous.daily_totals,
                week_total: data.week_total ?? previous.week_total,
            };
        });
    }, []);

    /**
     * Folds in one day of a superseded cell write, and nothing else — into the
     * row wherever the page holds it, a just-added row included. A row the
     * page no longer holds is left alone rather than brought back. A null cell
     * means the write emptied the row, so the day is cleared.
     */
    const applyCell = useCallback((key, date, cell) => {
        const fold = (rows) =>
            rows.map((row) =>
                row.key === key
                    ? { ...row, cells: { ...row.cells, [date]: cell ?? { ...row.cells[date], id: null, quantity: 0, note: null, type: null } } }
                    : row,
            );

        setPendingRows(fold);
        setPayload((previous) => (previous ? { ...previous, rows: fold(previous.rows) } : previous));
    }, []);

    // A row-level write rewrites every open day of the row, so no cell write
    // already in flight on that row may fold its day back over the result.
    const forgetRowCells = (key) => {
        delete cellSeq.current[key];
    };

    // `note` undefined keeps the day's current note; null removes it.
    const saveCell = (row, date, value, note) => {
        const seq = ++writeSeq.current;
        cellSeq.current[row.key] = { ...cellSeq.current[row.key], [date]: seq };
        setPendingWrites((count) => count + 1);

        return axios
            .put(props.entryUrl, {
                event_id: row.event.id,
                role_id: row.role_id,
                date,
                quantity: value,
                // A row whose days disagree reports no type at all, so the day's
                // own type is written back rather than retyping it silently.
                type: row.cells?.[date]?.type ?? row.type ?? 'internal',
                note: note !== undefined ? note : (row.cells?.[date]?.note ?? null),
            })
            .then(({ data }) => {
                if (seq !== writeSeq.current) {
                    if (cellSeq.current[row.key]?.[date] === seq) {
                        applyCell(row.key, date, data.data.row?.cells?.[date]);
                    }

                    return true;
                }

                applyWrite(row.key, data.data, date);

                return true;
            })
            .catch((e) => {
                toast.error(refusalMessage(e));

                // Resolved rather than rethrown so no caller sees an unhandled
                // rejection, but false so the note box knows its value is still
                // unsaved and keeps it pending.
                return false;
            })
            .finally(() => {
                setPendingWrites((count) => count - 1);
            });
    };

    const saveNote = (row, date, note) => {
        const cell = row.cells?.[date];

        // A day with nothing recorded has no entry to hang a note on, and a
        // zero quantity deletes rather than stores — so say so instead of
        // writing something that silently vanishes.
        if (!cell || !cell.quantity) {
            toast.error($t('Record time on this day before adding a note'));

            return Promise.resolve(false);
        }

        return saveCell(row, date, cell.quantity, note);
    };

    // Whether the server holds anything for the row. A just-added row counts
    // once any of its days carries an entry id: a first write whose response
    // was superseded folds only its own day in, leaving the row among the
    // pending ones although an entry now exists.
    const isSaved = (row) => !!payload?.rows?.some((existing) => existing.key === row.key) || Object.values(row.cells).some((cell) => !!cell?.id);

    const changeType = (row, type) => {
        if (!payload?.week) {
            return;
        }

        // A row with nothing recorded has no entries to retype, so the change
        // stays local until its first cell is filled.
        if (!isSaved(row)) {
            setPendingRows((previous) => previous.map((pending) => (pending.key === row.key ? { ...pending, type } : pending)));

            return;
        }

        const seq = ++writeSeq.current;
        forgetRowCells(row.key);
        setPendingWrites((count) => count + 1);

        axios
            .put(props.rowUpdateUrl, {
                week: payload.week.start,
                event_id: row.event.id,
                role_name: row.role_name,
                category: row.category,
                type,
            })
            .then(({ data }) => {
                if (seq !== writeSeq.current) return;

                applyWrite(row.key, data.data);
            })
            .catch((e) => {
                toast.error(refusalMessage(e));
            })
            .finally(() => {
                setPendingWrites((count) => count - 1);
            });
    };

    const removeRow = (row) => {
        if (!isSaved(row)) {
            setPendingRows((previous) => previous.filter((pending) => pending.key !== row.key));

            return;
        }

        confirmRemove({
            title: $t('Removing row - {{project}} · {{role}}', { project: row.event.name, role: row.role_name }),
            onConfirm: () => {
                const seq = ++writeSeq.current;
                forgetRowCells(row.key);
                setPendingWrites((count) => count + 1);

                return axios
                    .delete(props.rowDestroyUrl, {
                        data: {
                            week: payload.week.start,
                            event_id: row.event.id,
                            role_name: row.role_name,
                            category: row.category,
                        },
                    })
                    .then(({ data }) => {
                        if (seq !== writeSeq.current) return;

                        applyWrite(row.key, data.data);
                        toast.success($t('The row was removed'));
                    })
                    .catch((e) => {
                        toast.error(refusalMessage(e));
                    })
                    .finally(() => {
                        setPendingWrites((count) => count - 1);
                    });
            },
        });
    };

    const copyLastWeek = () => {
        const seq = ++writeSeq.current;
        setPendingWrites((count) => count + 1);

        axios
            .post(props.copyLastWeekUrl, { week: payload.week.start })
            .then(({ data }) => {
                if (seq !== writeSeq.current) return;

                const { skipped, ...week } = data.data;
                // The copy replaces the whole week, so no earlier cell write
                // may fold its day back over it.
                cellSeq.current = {};
                setPayload(week);
                setPendingRows([]);

                const locked = skipped?.locked ?? 0;
                const uncovered = skipped?.uncovered ?? 0;

                if (locked === 0 && uncovered === 0) {
                    toast.success($t('Last week was copied across'));
                } else {
                    toast.success($t('Last week was copied across. {{locked}} locked and {{uncovered}} uncovered days were skipped.', { locked, uncovered }));
                }
            })
            .catch((e) => {
                toast.error(refusalMessage(e));
            })
            .finally(() => {
                setPendingWrites((count) => count - 1);
            });
    };

    // Submitting and reopening both answer with the whole week, which replaces
    // the page's copy the way copying last week does.
    const changeSubmission = (url, message) => {
        const seq = ++writeSeq.current;
        setPendingWrites((count) => count + 1);

        axios
            .post(url, { week: payload.week.start })
            .then(({ data }) => {
                if (seq !== writeSeq.current) return;

                cellSeq.current = {};
                setPayload(data.data);
                setPendingRows([]);
                toast.success(message);
            })
            .catch((e) => {
                toast.error(refusalMessage(e));
            })
            .finally(() => {
                setPendingWrites((count) => count - 1);
            });
    };

    const submitWeek = () => changeSubmission(props.submitUrl, $t('Week submitted'));

    const reopenWeek = () => changeSubmission(props.reopenUrl, $t('Week reopened for editing'));

    /**
     * A project and role already on the week is one row, not two, so the pick
     * scrolls to the row that holds it and says why rather than silently doing
     * nothing.
     *
     * The row is found by walking the tagged nodes and comparing the attribute
     * rather than by an attribute selector: a row key carries the role name and
     * category verbatim, and those are free text, so a quote or a bracket in a
     * role name would break a selector built by interpolation.
     */
    const jumpToRow = (key) => {
        const nodes = gridRef.current?.querySelectorAll('[data-row-key]') ?? [];
        const node = Array.from(nodes).find((candidate) => candidate.getAttribute('data-row-key') === key);

        node?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        // With seven rows the target is usually already on screen, so the
        // scroll is a no-op and the jump would be invisible. The highlight is
        // what makes it legible either way.
        clearTimeout(highlightTimer.current);
        setHighlightedKey(key);
        highlightTimer.current = setTimeout(() => setHighlightedKey(null), HIGHLIGHT_MS);

        // Informational, not an error: this navigated somewhere, which is what
        // was asked for.
        toast.info($t('That project and role are already on this week'));
    };

    const addRow = ({ event_id, role, type }) => {
        const project = payload.project_options.find((candidate) => candidate.id === event_id);

        if (!project) {
            return;
        }

        // A new row's cells carry this project's own lock date rather than the
        // week-level flag: days[].locked only says the day is shut for every
        // project at once, which would leave a locked cell editable here.
        const cells = {};

        days.forEach((day) => {
            cells[day.date] = {
                id: null,
                quantity: 0,
                note: null,
                locked: !!project.time_locked_until && day.date <= project.time_locked_until,
                type: null,
            };
        });

        setPendingRows((previous) => [
            ...previous,
            {
                key: rowKeyFor(event_id, role.name, role.category),
                event: { id: project.id, name: project.name },
                role_id: role.id,
                role_name: role.name,
                category: role.category,
                type,
                total: 0,
                cells,
            },
        ]);
    };

    // A failed load leaves nothing to render, so the retry has to be here —
    // otherwise a transient failure strands the page until a browser reload.
    if (error && !payload) {
        return (
            <div className="tw-space-y-4">
                <Alert type="error">{error}</Alert>
                <Button variant="secondary" disabled={loading} onClick={() => fetchWeek(requestedWeek)}>
                    {$t('Try again')}
                </Button>
            </div>
        );
    }

    if (loading && !payload) {
        return <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>;
    }

    if (!payload) {
        return null;
    }

    return (
        <div className="tw-space-y-4">
            {/* A failure after the first successful load keeps the week on
                screen — the page stays usable and the banner explains itself. */}
            {!!error && <Alert type="error">{error}</Alert>}

            <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
                <h1 className="table__header tw-mb-0">{$t('My Time Record')}</h1>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                    <Button variant="secondary" small disabled={busy} onClick={() => setRequestedWeek(payload.week.previous)} aria-label={$t('Previous week')}>
                        <ChevronLeftIcon className="tw-h-3 tw-w-3" />
                    </Button>
                    <TimeWeekPicker calendarUrl={props.calendarUrl} week={payload.week} disabled={busy} onPick={setRequestedWeek} />
                    <Button variant="secondary" small disabled={busy} onClick={() => setRequestedWeek(payload.week.next)} aria-label={$t('Next week')}>
                        <ChevronRightIcon className="tw-h-3 tw-w-3" />
                    </Button>

                    <Button variant="secondary" disabled={busy || weekSubmitted || !payload.rate_card.present} onClick={copyLastWeek}>
                        <CopyIcon className="tw-h-3 tw-w-3" />
                        {$t('Copy last week')}
                    </Button>
                </div>
            </div>

            {!payload.rate_card.present && (
                <Alert type="warning">{$t('No rate card has been set up for this organisation - contact your administrator')}</Alert>
            )}

            {payload.rate_card.present && noCoverage && (
                <Alert type="warning">{$t('No rate card is in force during this week, so nothing can be recorded on it.')}</Alert>
            )}

            {weekSubmitted && (
                <Alert type="info" role="status">
                    {$t('This week has been submitted. Reopen it to make changes.')}
                </Alert>
            )}

            {weekIsClosed && (
                <Alert type="info" role="status">
                    {$t('Every day this week is on or before the lock date on your projects, so this week is read-only.')}
                </Alert>
            )}

            <div className="table__card">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-6">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                        <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-0">{payload.week.label}</h2>
                        <TimeWeekStatusPill status={weekStatus} />
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                        <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                            {$t('Week total')} — <span className="tw-font-semibold">{formatTotal(payload.week_total, options)}</span> {unitLabel(options)}
                        </p>
                        {weekSubmitted ? (
                            <Button variant="secondary" disabled={busy} onClick={reopenWeek}>
                                {$t('Reopen week')}
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                disabled={busy || !canSubmit}
                                onClick={submitWeek}
                                title={
                                    weekStatus === 'future'
                                        ? $t('This week has not started yet')
                                        : weekStatus === 'empty'
                                          ? $t('Record some time before submitting this week')
                                          : undefined
                                }
                            >
                                {$t('Submit week')}
                            </Button>
                        )}
                    </div>
                </div>

                <TimeRecordGrid
                    week={payload.week}
                    days={days}
                    rows={rows}
                    options={options}
                    dailyTotals={payload.daily_totals}
                    weekTotal={payload.week_total}
                    containerRef={gridRef}
                    highlightedKey={highlightedKey}
                    weekSubmitted={weekSubmitted}
                    onCellSave={(row, date, value) => saveCell(row, date, value)}
                    onNoteSave={saveNote}
                    onTypeChange={changeType}
                    onRemove={removeRow}
                />
            </div>

            <AddTimeRowForm
                projects={payload.project_options}
                roles={payload.role_options}
                existingKeys={rows.map((row) => row.key)}
                readOnly={weekSubmitted}
                onAdd={addRow}
                onDuplicate={jumpToRow}
            />
        </div>
    );
}

if (document.getElementById('MyTimeRecord')) {
    const rootEl = document.getElementById('MyTimeRecord');

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <MyTimeRecordIndex
                    dataUrl={rootEl.getAttribute('data-data-url')}
                    entryUrl={rootEl.getAttribute('data-entry-url')}
                    rowUpdateUrl={rootEl.getAttribute('data-row-update-url')}
                    rowDestroyUrl={rootEl.getAttribute('data-row-destroy-url')}
                    copyLastWeekUrl={rootEl.getAttribute('data-copy-last-week-url')}
                    calendarUrl={rootEl.getAttribute('data-calendar-url')}
                    submitUrl={rootEl.getAttribute('data-submit-url')}
                    reopenUrl={rootEl.getAttribute('data-reopen-url')}
                    initialWeek={rootEl.getAttribute('data-initial-week')}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
